import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../data/translations';
import { referenceImages } from '../data/references';

export const ReferencesTeaser = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const featuredRefs = referenceImages.slice(0, 6);

  const getCategoryLabel = (cat) => {
    return t.references[cat] || cat;
  };

  return (
    <section className="py-20 md:py-28 bg-[#111111]" data-testid="references-teaser-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-testid="references-title">{t.references.title}</h2>
            <p className="text-gray-400 max-w-xl">{t.references.subtitle}</p>
          </div>
          <Link to="/referenzen" className="mt-6 md:mt-0 inline-flex items-center space-x-2 text-[#E31C23] font-semibold hover:text-[#B91219] transition-colors group" data-testid="references-view-all">
            <span>{t.references.all}</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {featuredRefs.map((ref, index) => (
            <motion.div key={ref.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.1 }} className={`group relative overflow-hidden cursor-pointer ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`} data-testid={`reference-item-${index}`}>
              <div className={`relative ${index === 0 ? 'h-[400px] md:h-[500px]' : 'h-48 md:h-56'}`}>
                <img src={ref.image} alt={ref.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-colors duration-300" />
                <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-[#E31C23] text-xs font-semibold uppercase tracking-wider mb-2">{getCategoryLabel(ref.category)}</span>
                  <h3 className="font-heading text-white text-lg font-bold">{ref.title}</h3>
                  {index === 0 && <p className="text-gray-300 text-sm mt-2">{ref.description}</p>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }} className="text-center mt-12">
          <Link to="/kontakt" className="inline-flex items-center space-x-2 bg-[#E31C23] text-white px-8 py-4 font-semibold hover:bg-[#B91219] transition-colors duration-300" data-testid="references-cta">
            <span>{t.references.cta}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
